import {createEntityAdapter, EntityAdapter, EntityState, Update} from '@ngrx/entity';
import {Lesson} from '../models/lesson.model';
import {compareLessons} from '../common/sort-model';
import {moveItemInArray, transferArrayItem} from '@angular/cdk/drag-drop';
import {LessonActions} from './action-types';
import {createReducer, on} from '@ngrx/store';
import OCHSimpleChanges, {LessonRiskyProperties} from "../shared/utils/och-simple-changes";
import {deepClone} from "../common/collection-utils";


export interface State extends EntityState<Lesson> {
  loadedSections: { [sectionId: string]: boolean },
  activeLessonId: string,
  pendingLessonReordering: Update<Lesson>[],
  uploadsOngoing: number;
  temporaryFormStates: { [lessonId: string]: any };
}

export const adapter: EntityAdapter<Lesson> = createEntityAdapter<Lesson>({
  sortComparer: compareLessons
});

export const initialState: State = adapter.getInitialState({
  loadedSections: {},
  activeLessonId: undefined,
  pendingLessonReordering: [],
  uploadsOngoing: 0,
  temporaryFormStates: {}
});


export const lessonReducer = createReducer(

  initialState,

  on(LessonActions.watchLesson, (state,action) => {
    return {
      ...state,
      activeLessonId: action.lessonId
    };
  }),

  on(LessonActions.addLesson, (state,action) => adapter.addOne(action.lesson, state)),

  on(LessonActions.courseLessonsLoaded, (state, action) => {
    const lessons = adapter.addMany(action.lessons, state);
    const newLoadedSections = { ...state.loadedSections };
    action.lessons.forEach(l => { if (l.sectionId) newLoadedSections[l.sectionId] = true; });
    action.sectionIds?.forEach(id => { newLoadedSections[id] = true; });
    return { ...lessons, loadedSections: newLoadedSections };
  }),

  on(LessonActions.updateLesson, (state,action) => adapter.updateOne(action.lesson, state)),

  on(LessonActions.updateLessonSynchronizedClones, (state, action) => {

    const lessonId = action.lesson.id;
    const courseId = action.courseId;
    const lesson = action.lesson.changes;
    const simpleChanges = OCHSimpleChanges.getInstance();
    simpleChanges.setRiskyLessonProperties(LessonRiskyProperties)

    // search lesson with a sourceLessonId == ${lessonId}...
    const cloneLessonIds: Update<Lesson>[] = Object.entries(state.entities).filter(([id, lesson]) => {

      if(lesson.sourceLessonId === lessonId) {
        return true;
      }

      return false;

    }).map(([id, currentLesson]) => {

      const changes = simpleChanges.findChanges(deepClone(currentLesson), lesson);
      const lessonChanges = simpleChanges.mergeChanges(changes)

      console.log("updateLessonSynchronizedClones map", id, currentLesson, changes, lessonChanges)

      return {
        id,
        changes: lessonChanges//{ ...currentLesson, ...lessonChanges },
      }

    });
    console.log("updateLessonSynchronizedClones cloneLessonIds", cloneLessonIds)

    return adapter.updateMany(cloneLessonIds, state)

  }),

  on(LessonActions.deleteLesson, (state,action) => adapter.removeOne(action.id, state)),

  on(LessonActions.deleteLessonSynchronizedClones, (state, action) => {

    const lessonId = action.id;

    const deleteLessonSynchronizedClonesIds: string[] = []

    // search lesson with a sourceLessonId == ${lessonId}...

    Object.entries(state.entities).forEach(([id, lesson]) => {
      if(lesson.sourceLessonId === lessonId) {
        deleteLessonSynchronizedClonesIds.push(id);
      }
    })

    return adapter.removeMany(deleteLessonSynchronizedClonesIds, state)

  }),

  on(LessonActions.loadLessonVideo, (state,action) => adapter.updateOne(action.update, state)),

  on(LessonActions.updateLessonOrder, (state,action) => {

    const newSectionLessons = [...action.newSectionLessons],
      previousSectionLessons = [...action.previousSectionLessons];

    const sectionChanged = (action.previousSectionId !== action.newSectionId);

    if (sectionChanged) {
      transferArrayItem(previousSectionLessons, newSectionLessons, action.previousIndex, action.currentIndex);
    } else {
      moveItemInArray( newSectionLessons, action.previousIndex, action.currentIndex);
    }

    // Compute seqNo changes for all lessons (including virtual course-completion)
    // so the store reflects the correct order after drag.
    const allChanges = [
      ...calculateSectionChanges(newSectionLessons, action.newSectionId),
      ...(sectionChanged ? calculateSectionChanges(previousSectionLessons, action.previousSectionId) : []),
    ];

    // Only real (Firestore-backed) lessons go to pendingLessonReordering for persistence.
    const firestoreChanges = allChanges.filter(change => state.entities[change.id]?.type !== 'course-completion');

    return adapter.updateMany(allChanges, {...state, pendingLessonReordering: firestoreChanges});
  }),

  on(LessonActions.updateLessonOrderCompleted, (state,action) => {
    return {
      ...state,
      pendingLessonReordering: []
    };
  }),

  on(LessonActions.uploadStarted, (state,action) => {
    return {
      ...state,
      uploadsOngoing: state.uploadsOngoing + 1
    };
  }),

  on(LessonActions.uploadFinished, (state,action) => {
    return {
      ...state,
      uploadsOngoing: state.uploadsOngoing - 1
    };
  }),


  on(LessonActions.publishLesson, (state, action) => adapter.updateOne({
    id: action.lessonId,
    changes: {
      status: "published"
    }
  },state )),

  on(LessonActions.unpublishLesson, (state, action) => adapter.updateOne({
    id: action.lessonId,
    changes: {
      status: "draft"
    }
  },state )),

  on(LessonActions.saveLessonFormState, (state, action) => {
    return {
      ...state,
      temporaryFormStates: {
        ...state.temporaryFormStates,
        [action.lessonId]: action.formState
      }
    };
  }),

  on(LessonActions.clearLessonFormState, (state, action) => {
    const newFormStates = { ...state.temporaryFormStates };
    delete newFormStates[action.lessonId];
    return {
      ...state,
      temporaryFormStates: newFormStates
    };
  })


);

function calculateSectionChanges(sectionLessonsInNewOrder: Lesson[], sectionId:string): Update<Lesson>[] {
  let lessonSeqNoCounter = 1;

  const reorderChanges: Update<Lesson>[] = [];

  sectionLessonsInNewOrder.forEach(lesson => {

    let changes: Partial<Lesson>;

    changes = {
      seqNo: lessonSeqNoCounter,
      sectionId
    };

    reorderChanges.push({id: lesson.id, changes});

    lessonSeqNoCounter++;

  });

  return reorderChanges;

}


export const {
  selectIds,
  selectEntities,
  selectAll,
  selectTotal,
} = adapter.getSelectors();







